import { Injectable } from '@angular/core';
import {formatUnit, Internationalization, cldrData, L10n} from "@syncfusion/ej2-base";
import {DateFormatOptions, NumberFormatOptions} from "@syncfusion/ej2-base/src/internationalization";
import {InternationalizationNumberFormatOptions} from "./internationalization-number-format-options";
import {InternationalizationDateFormatOptions} from "./internationalization-date-format-options";
import {Observer, Subject, Subscription} from "rxjs";
import {setCulture, setCurrencyCode, loadCldr} from '@syncfusion/ej2-base';
import {LocalizeService} from "./localize.service";
import { DateTime } from "luxon";

// CLDR data

import * as numberingSystems from 'cldr-core/supplemental/numberingSystems.json';
import * as weekData from 'cldr-core/supplemental/weekData.json';
import * as currencyData from 'cldr-core/supplemental/currencyData.json';

// en_US is the default and is also built in
import * as gregorian_en_US from 'cldr-dates-modern/main/en/ca-gregorian.json';
import * as numbers_en_US from 'cldr-numbers-modern/main/en/numbers.json';
import * as timeZoneNames_en_US from 'cldr-dates-modern/main/en/timeZoneNames.json';
import * as currencies_en_US from 'cldr-numbers-modern/main/en/currencies.json';

// en_AU
import * as gregorian_en_AU from 'cldr-dates-modern/main/en-AU/ca-gregorian.json';
import * as numbers_en_AU from 'cldr-numbers-modern/main/en-AU/numbers.json';
import * as timeZoneNames_en_AU from 'cldr-dates-modern/main/en-AU/timeZoneNames.json';
import * as currencies_en_AU from 'cldr-numbers-modern/main/en-AU/currencies.json';

// en_GB
import * as gregorian_en_GB from 'cldr-dates-modern/main/en-GB/ca-gregorian.json';
import * as numbers_en_GB from 'cldr-numbers-modern/main/en-GB/numbers.json';
import * as timeZoneNames_en_GB from 'cldr-dates-modern/main/en-GB/timeZoneNames.json';
import * as currencies_en_GB from 'cldr-numbers-modern/main/en-GB/currencies.json';

// en_ZA
import * as gregorian_en_ZA from 'cldr-dates-modern/main/en-ZA/ca-gregorian.json';
import * as numbers_en_ZA from 'cldr-numbers-modern/main/en-ZA/numbers.json';
import * as timeZoneNames_en_ZA from 'cldr-dates-modern/main/en-ZA/timeZoneNames.json';
import * as currencies_en_ZA from 'cldr-numbers-modern/main/en-ZA/currencies.json';

// fr_FR
import * as gregorian_fr_FR from 'cldr-dates-modern/main/fr/ca-gregorian.json';
import * as numbers_fr_FR from 'cldr-numbers-modern/main/fr/numbers.json';
import * as timeZoneNames_fr_FR from 'cldr-dates-modern/main/fr/timeZoneNames.json';
import * as currencies_fr_FR from 'cldr-numbers-modern/main/fr/currencies.json';

// de_DE
import * as gregorian_de_DE from 'cldr-dates-modern/main/de/ca-gregorian.json';
import * as numbers_de_DE from 'cldr-numbers-modern/main/de/numbers.json';
import * as timeZoneNames_de_DE from 'cldr-dates-modern/main/de/timeZoneNames.json';
import * as currencies_de_DE from 'cldr-numbers-modern/main/de/currencies.json';

// nl_NL
import * as gregorian_nl_NL from 'cldr-dates-modern/main/nl/ca-gregorian.json';
import * as numbers_nl_NL from 'cldr-numbers-modern/main/nl/numbers.json';
import * as timeZoneNames_nl_NL from 'cldr-dates-modern/main/nl/timeZoneNames.json';
import * as currencies_nl_NL from 'cldr-numbers-modern/main/nl/currencies.json';

// es_ES
import * as gregorian_es_ES from 'cldr-dates-modern/main/es/ca-gregorian.json';
import * as numbers_es_ES from 'cldr-numbers-modern/main/es/numbers.json';
import * as timeZoneNames_es_ES from 'cldr-dates-modern/main/es/timeZoneNames.json';
import * as currencies_es_ES from 'cldr-numbers-modern/main/es/currencies.json';

// es_CL
import * as gregorian_es_CL from 'cldr-dates-modern/main/es-CL/ca-gregorian.json';
import * as numbers_es_CL from 'cldr-numbers-modern/main/es-CL/numbers.json';
import * as timeZoneNames_es_CL from 'cldr-dates-modern/main/es-CL/timeZoneNames.json';
import * as currencies_es_CL from 'cldr-numbers-modern/main/es-CL/currencies.json';

// pt_BR
import * as gregorian_pt_BR from 'cldr-dates-modern/main/pt/ca-gregorian.json';
import * as numbers_pt_BR from 'cldr-numbers-modern/main/pt/numbers.json';
import * as timeZoneNames_pt_BR from 'cldr-dates-modern/main/pt/timeZoneNames.json';
import * as currencies_pt_BR from 'cldr-numbers-modern/main/pt/currencies.json';

// pt_PT
import * as gregorian_pt_PT from 'cldr-dates-modern/main/pt-PT/ca-gregorian.json';
import * as numbers_pt_PT from 'cldr-numbers-modern/main/pt-PT/numbers.json';
import * as timeZoneNames_pt_PT from 'cldr-dates-modern/main/pt-PT/timeZoneNames.json';
import * as currencies_pt_PT from 'cldr-numbers-modern/main/pt-PT/currencies.json';

// af_ZA
import * as gregorian_af_ZA from 'cldr-dates-modern/main/af/ca-gregorian.json';
import * as numbers_af_ZA from 'cldr-numbers-modern/main/af/numbers.json';
import * as timeZoneNames_af_ZA from 'cldr-dates-modern/main/af/timeZoneNames.json';
import * as currencies_af_ZA from 'cldr-numbers-modern/main/af/currencies.json';

// id_ID
import * as gregorian_id_ID from 'cldr-dates-modern/main/id/ca-gregorian.json';
import * as numbers_id_ID from 'cldr-numbers-modern/main/id/numbers.json';
import * as timeZoneNames_id_ID from 'cldr-dates-modern/main/id/timeZoneNames.json';
import * as currencies_id_ID from 'cldr-numbers-modern/main/id/currencies.json';

// ru_RU
import * as gregorian_ru_RU from 'cldr-dates-modern/main/ru/ca-gregorian.json';
import * as numbers_ru_RU from 'cldr-numbers-modern/main/ru/numbers.json';
import * as timeZoneNames_ru_RU from 'cldr-dates-modern/main/ru/timeZoneNames.json';
import * as currencies_ru_RU from 'cldr-numbers-modern/main/ru/currencies.json';

// hy_AM
import * as gregorian_hy_AM from 'cldr-dates-modern/main/hy/ca-gregorian.json';
import * as numbers_hy_AM from 'cldr-numbers-modern/main/hy/numbers.json';
import * as timeZoneNames_hy_AM from 'cldr-dates-modern/main/hy/timeZoneNames.json';
import * as currencies_hy_AM from 'cldr-numbers-modern/main/hy/currencies.json';

// uk_UA
import * as gregorian_uk_UA from 'cldr-dates-modern/main/uk/ca-gregorian.json';
import * as numbers_uk_UA from 'cldr-numbers-modern/main/uk/numbers.json';
import * as timeZoneNames_uk_UA from 'cldr-dates-modern/main/uk/timeZoneNames.json';
import * as currencies_uk_UA from 'cldr-numbers-modern/main/uk/currencies.json';

// th_TH
import * as gregorian_th_TH from 'cldr-dates-modern/main/th/ca-gregorian.json';
import * as numbers_th_TH from 'cldr-numbers-modern/main/th/numbers.json';
import * as timeZoneNames_th_TH from 'cldr-dates-modern/main/th/timeZoneNames.json';
import * as currencies_th_TH from 'cldr-numbers-modern/main/th/currencies.json';

// lo_LA
import * as gregorian_lo_LA from 'cldr-dates-modern/main/lo/ca-gregorian.json';
import * as numbers_lo_LA from 'cldr-numbers-modern/main/lo/numbers.json';
import * as timeZoneNames_lo_LA from 'cldr-dates-modern/main/lo/timeZoneNames.json';
import * as currencies_lo_LA from 'cldr-numbers-modern/main/lo/currencies.json';

// tr_TR
import * as gregorian_tr_TR from 'cldr-dates-modern/main/tr/ca-gregorian.json';
import * as numbers_tr_TR from 'cldr-numbers-modern/main/tr/numbers.json';
import * as timeZoneNames_tr_TR from 'cldr-dates-modern/main/tr/timeZoneNames.json';
import * as currencies_tr_TR from 'cldr-numbers-modern/main/tr/currencies.json';

// zh_CN
import * as gregorian_zh_CN from 'cldr-dates-modern/main/zh/ca-gregorian.json';
import * as numbers_zh_CN from 'cldr-numbers-modern/main/zh/numbers.json';
import * as timeZoneNames_zh_CN from 'cldr-dates-modern/main/zh/timeZoneNames.json';
import * as currencies_zh_CN from 'cldr-numbers-modern/main/zh/currencies.json';

// pl_PL
import * as gregorian_pl_PL from 'cldr-dates-modern/main/pl/ca-gregorian.json';
import * as numbers_pl_PL from 'cldr-numbers-modern/main/pl/numbers.json';
import * as timeZoneNames_pl_PL from 'cldr-dates-modern/main/pl/timeZoneNames.json';
import * as currencies_pl_PL from 'cldr-numbers-modern/main/pl/currencies.json';

// it_IT
import * as gregorian_it_IT from 'cldr-dates-modern/main/it/ca-gregorian.json';
import * as numbers_it_IT from 'cldr-numbers-modern/main/it/numbers.json';
import * as timeZoneNames_it_IT from 'cldr-dates-modern/main/it/timeZoneNames.json';
import * as currencies_it_IT from 'cldr-numbers-modern/main/it/currencies.json';

// cs_CZ
import * as gregorian_cs_CZ from 'cldr-dates-modern/main/cs/ca-gregorian.json';
import * as numbers_cs_CZ from 'cldr-numbers-modern/main/cs/numbers.json';
import * as timeZoneNames_cs_CZ from 'cldr-dates-modern/main/cs/timeZoneNames.json';
import * as currencies_cs_CZ from 'cldr-numbers-modern/main/cs/currencies.json';

// L10n data

// @ts-ignore
import { default as l10n_en_US } from "@syncfusion/ej2-locale/src/en-US.json";
// @ts-ignore
import { default as l10n_de_DE } from "@syncfusion/ej2-locale/src/de.json";
// @ts-ignore
import { default as l10n_fr_FR } from "@syncfusion/ej2-locale/src/fr.json";
// @ts-ignore
import { default as l10n_nl_NL } from "@syncfusion/ej2-locale/src/nl.json";
// @ts-ignore
import { default as l10n_es_ES } from "@syncfusion/ej2-locale/src/es.json";
// @ts-ignore
import { default as l10n_pt_BR } from "@syncfusion/ej2-locale/src/pt-BR.json";
// @ts-ignore
import { default as l10n_pt_PT } from "@syncfusion/ej2-locale/src/pt.json";
// @ts-ignore
import { default as l10n_id_ID } from "@syncfusion/ej2-locale/src/id.json";
// @ts-ignore
import { default as l10n_ru_RU } from "@syncfusion/ej2-locale/src/ru.json";
// @ts-ignore
import { default as l10n_th_TH } from "@syncfusion/ej2-locale/src/th.json";
// @ts-ignore
import { default as l10n_tr_TR } from "@syncfusion/ej2-locale/src/tr.json";
// @ts-ignore
import { default as l10n_zh_CN } from "@syncfusion/ej2-locale/src/zh.json";
// @ts-ignore
import { default as l10n_pl_PL } from "@syncfusion/ej2-locale/src/pl.json";
// @ts-ignore
import { default as l10n_it_IT } from "@syncfusion/ej2-locale/src/it.json";
// @ts-ignore
import { default as l10n_cs_CZ } from "@syncfusion/ej2-locale/src/cs.json";
// @ts-ignore
import { default as l10n_af_ZA } from "./locale/af.json";
// @ts-ignore
import { default as l10n_es_CL } from "./locale/es-CL.json";
// @ts-ignore
import { default as l10n_hy_AM } from "./locale/hy.json";
// @ts-ignore
import { default as l10n_uk_UA } from "./locale/uk.json";
// @ts-ignore
import { default as l10n_lo_LA } from "./locale/lo.json";

import {InternationalizationFieldData, InternationalizationFieldValue} from "./internationalization-field-value";

export type LocaleKeyValue = { i18nKey: string, value: string };

export enum DateTimeFormatSkeleton {
  SHORT_DATE = 'short',
  LONG_DATE = 'yMMMEd',
  SHORT_TIME= 'short',
  LONG_TIME = 'medium',
  SHORT_DATE_TIME = 'short',
  LONG_DATE_TIME = 'medium'
}

export enum DateTimeFormatMoment {
  SHORT_TIME= 'HH:mm',
  LONG_TIME = 'HH:mm:ss',
  SHORT_DATE = 'DD/MM/YY',
  LONG_DATE = 'ddd DD MMM YYYY'
}

interface LocaleData {
  calendar: any;
  numbers: any;
  timeZoneNames: any;
  currencies: any;
  l10n: any;
  culture: string;
}

export interface LocaleCustomization {
  Locale: string;

  ShortDateFormat: string;
  LongDateFormat: string;
  ShortTimeFormat: string;
  LongTimeFormat: string;
  ShortDateTimeFormat: string;
  LongDateTimeFormat: string;

  ShortDateFormatIsSkeleton: boolean;
  LongDateFormatIsSkeleton: boolean;
  ShortTimeFormatIsSkeleton: boolean;
  LongTimeFormatIsSkeleton: boolean;
  ShortDateTimeFormatIsSkeleton: boolean;
  LongDateTimeFormatIsSkeleton: boolean;
}

type LocaleString = `${string}_${string}` | `${string}-${string}` | string;

interface LocalePair {
  language: string; // Standard underscore locale identifiers, e.g. 'en_AU'
  culture: string; // Syncfusion locale identifiers, e.g. 'en' or 'en-GB'
}

@Injectable({
  providedIn: 'root'
})
export class InternationalizationService {

  DateTimeFormatSkeleton = DateTimeFormatSkeleton;

  DateTimeFormatMoment = DateTimeFormatMoment;

  DEFAULT_LOCALE = 'en_US';

  SUPPORTED_LOCALES: {[key: string]: LocaleData} = {
    en_US: {
      calendar: gregorian_en_US['default'],
      numbers: numbers_en_US['default'],
      timeZoneNames: timeZoneNames_en_US['default'],
      currencies: currencies_en_US['default'],
      l10n: l10n_en_US["en-US"],
      culture: 'en',
    },
    en_AU: {
      calendar: gregorian_en_AU['default'],
      numbers: numbers_en_AU['default'],
      timeZoneNames: timeZoneNames_en_AU['default'],
      currencies: currencies_en_AU['default'],
      l10n: l10n_en_US["en-US"],
      culture: 'en-AU',
    },
    en_GB: {
      calendar: gregorian_en_GB['default'],
      numbers: numbers_en_GB['default'],
      timeZoneNames: timeZoneNames_en_GB['default'],
      currencies: currencies_en_GB['default'],
      l10n: l10n_en_US["en-US"],
      culture: 'en-GB',
    },
    en_ZA: {
      calendar: gregorian_en_ZA['default'],
      numbers: numbers_en_ZA['default'],
      timeZoneNames: timeZoneNames_en_ZA['default'],
      currencies: currencies_en_ZA['default'],
      l10n: l10n_en_US["en-US"],
      culture: 'en-ZA',
    },
    fr_FR: {
      calendar: gregorian_fr_FR['default'],
      numbers: numbers_fr_FR['default'],
      timeZoneNames: timeZoneNames_fr_FR['default'],
      currencies: currencies_fr_FR['default'],
      l10n: l10n_fr_FR.fr,
      culture: 'fr',
    },
    de_DE: {
      calendar: gregorian_de_DE['default'],
      numbers: numbers_de_DE['default'],
      timeZoneNames: timeZoneNames_de_DE['default'],
      currencies: currencies_de_DE['default'],
      l10n: l10n_de_DE.de,
      culture: 'de',
    },
    nl_NL: {
      calendar: gregorian_nl_NL['default'],
      numbers: numbers_nl_NL['default'],
      timeZoneNames: timeZoneNames_nl_NL['default'],
      currencies: currencies_nl_NL['default'],
      l10n: l10n_nl_NL.nl,
      culture: 'nl',
    },
    es_ES: {
      calendar: gregorian_es_ES['default'],
      numbers: numbers_es_ES['default'],
      timeZoneNames: timeZoneNames_es_ES['default'],
      currencies: currencies_es_ES['default'],
      l10n: l10n_es_ES.es,
      culture: 'es',
    },
    es_CL: {
      calendar: gregorian_es_CL['default'],
      numbers: numbers_es_CL['default'],
      timeZoneNames: timeZoneNames_es_CL['default'],
      currencies: currencies_es_CL['default'],
      l10n: l10n_es_CL["es-CL"],
      culture: 'es-CL',
    },
    pt_BR: {
      calendar: gregorian_pt_BR['default'],
      numbers: numbers_pt_BR['default'],
      timeZoneNames: timeZoneNames_pt_BR['default'],
      currencies: currencies_pt_BR['default'],
      l10n: l10n_pt_BR["pt-BR"],
      culture: 'pt',
    },
    pt_PT: {
      calendar: gregorian_pt_PT['default'],
      numbers: numbers_pt_PT['default'],
      timeZoneNames: timeZoneNames_pt_PT['default'],
      currencies: currencies_pt_PT['default'],
      l10n: l10n_pt_PT["pt-PT"],
      culture: 'pt-PT',
    },
    af_ZA: {
      calendar: gregorian_af_ZA['default'],
      numbers: numbers_af_ZA['default'],
      timeZoneNames: timeZoneNames_af_ZA['default'],
      currencies: currencies_af_ZA['default'],
      l10n: l10n_af_ZA.af,
      culture: 'af',
    },
    id_ID: {
      calendar: gregorian_id_ID['default'],
      numbers: numbers_id_ID['default'],
      timeZoneNames: timeZoneNames_id_ID['default'],
      currencies: currencies_id_ID['default'],
      l10n: l10n_id_ID.id,
      culture: 'id',
    },
    ru_RU: {
      calendar: gregorian_ru_RU['default'],
      numbers: numbers_ru_RU['default'],
      timeZoneNames: timeZoneNames_ru_RU['default'],
      currencies: currencies_ru_RU['default'],
      l10n: l10n_ru_RU.ru,
      culture: 'ru',
    },
    hy_AM: {
      calendar: gregorian_hy_AM['default'],
      numbers: numbers_hy_AM['default'],
      timeZoneNames: timeZoneNames_hy_AM['default'],
      currencies: currencies_hy_AM['default'],
      l10n: l10n_hy_AM.hy,
      culture: 'hy',
    },
    uk_UA: {
      calendar: gregorian_uk_UA['default'],
      numbers: numbers_uk_UA['default'],
      timeZoneNames: timeZoneNames_uk_UA['default'],
      currencies: currencies_uk_UA['default'],
      l10n: l10n_uk_UA.uk,
      culture: 'uk',
    },
    th_TH: {
      calendar: gregorian_th_TH['default'],
      numbers: numbers_th_TH['default'],
      timeZoneNames: timeZoneNames_th_TH['default'],
      currencies: currencies_th_TH['default'],
      l10n: l10n_th_TH.th,
      culture: 'th',
    },
    lo_LA: {
      calendar: gregorian_lo_LA['default'],
      numbers: numbers_lo_LA['default'],
      timeZoneNames: timeZoneNames_lo_LA['default'],
      currencies: currencies_lo_LA['default'],
      l10n: l10n_lo_LA.lo,
      culture: 'lo',
    },
    tr_TR: {
      calendar: gregorian_tr_TR['default'],
      numbers: numbers_tr_TR['default'],
      timeZoneNames: timeZoneNames_tr_TR['default'],
      currencies: currencies_tr_TR['default'],
      l10n: l10n_tr_TR.tr,
      culture: 'tr',
    },
    zh_CN: {
      calendar: gregorian_zh_CN['default'],
      numbers: numbers_zh_CN['default'],
      timeZoneNames: timeZoneNames_zh_CN['default'],
      currencies: currencies_zh_CN['default'],
      l10n: l10n_zh_CN.zh,
      culture: 'zh',
    },
    pl_PL: {
      calendar: gregorian_pl_PL['default'],
      numbers: numbers_pl_PL['default'],
      timeZoneNames: timeZoneNames_pl_PL['default'],
      currencies: currencies_pl_PL['default'],
      l10n: l10n_pl_PL.pl,
      culture: 'pl',
    },
    it_IT: {
      calendar: gregorian_it_IT['default'],
      numbers: numbers_it_IT['default'],
      timeZoneNames: timeZoneNames_it_IT['default'],
      currencies: currencies_it_IT['default'],
      l10n: l10n_it_IT.it,
      culture: 'it',
    },
    cs_CZ: {
      calendar: gregorian_cs_CZ['default'],
      numbers: numbers_cs_CZ['default'],
      timeZoneNames: timeZoneNames_cs_CZ['default'],
      currencies: currencies_cs_CZ['default'],
      l10n: l10n_cs_CZ.cs,
      culture: 'cs',
    },
  };

  i18n: Internationalization;

  cldrData: any;

  baseLanguageCode: string = "en";

  L10n: any;

  localeChanged$: Subject<string> = new Subject<string>();

  constructor(private localize: LocalizeService) {
    (window as any).NG_SHARED_INTERNATIONALIZATION_SERVICE = this;
  }

  init(locale?: string) {
    this.loadLocale(locale);
    this.setLocale(locale);
  }

  /**
   * Load cldr data for a specific locale. It must be one of our supported ones.
   */
  loadLocale(locale?: LocaleString): string {
    const { language, culture } = this.lookupLocale(locale);

    if (!this?.cldrData?.main?.[culture]) {
      loadCldr(
        numberingSystems['default'],
        weekData['default'],
        currencyData['default'],
        this.SUPPORTED_LOCALES[language].calendar,
        this.SUPPORTED_LOCALES[language].numbers,
        this.SUPPORTED_LOCALES[language].timeZoneNames,
        this.SUPPORTED_LOCALES[language].currencies,
      );

      const options = {};
      options[culture] = this.SUPPORTED_LOCALES[language].l10n;
      L10n.load(options);

    } else {
      console.debug(`InternationalizationService: CLDR data for ${language} has already been loaded`);
    }

    return language;
  }

  /**
   * Set the current locale
   */
  setLocale(locale?: LocaleString, broadcast: boolean = false) {
    const { language, culture } = this.lookupLocale(locale);
    setCulture(this.SUPPORTED_LOCALES[language].culture);
    setCurrencyCode(this.getSystemDefaultCurrencyCode());
    this.cldrData = cldrData;
    this.L10n = L10n;
    this.i18n =new Internationalization();

    this.baseLanguageCode = this.getCulture().split("-")[0];

    if (broadcast) {

      // We need to update the Angular and Knockout user services, the KO i18n service and the nav menu
      // NOTE: Done this way via the property on 'window' to break a circular dependency.
      (window as any).NG_SHARED_USER_SERVICE.setLanguage(locale);

      // Set in the server as well.
      // NOTE: Done this way via the property on 'window' to break a circular dependency.
      const requestService = (window as any).NG_SHARED_REQUEST_SERVICE;

      requestService.createRequest()
        .controller("servicely.core.Application")
        .method("setLocale")
        .data({locale})
        .successFunc((response) => {
          $.ajax("/_support/i18n.json?locale=" + (locale != null ? locale : "")).then((values) => {
            const bridge = (window as any).KO_INTERNATIONALIZATION_BRIDGE;
            bridge.setLocale(bridge.getDefaultLocale(), locale, values.data, values.pluralRule);

            // Notify interested Angular components that the locale has changed. Most Syncfusion components should handle that already.
            this.localeChanged$.next(this.SUPPORTED_LOCALES[language].culture);

            // Refresh the Knockout side using the message bus.
            const MessageBus = (window as any).MessageBus;
            MessageBus.publish(MessageBus.CHANNELS.REFRESH_EVENT, "*");
            MessageBus.publish(MessageBus.CHANNELS.MESSAGE_BUS_BRIDGE_API, "RELOAD_NAVIGATION_MENU");
          });

        })
        .errorFunc((error) => {
          console.error(`InternationalizationService: unable to set user locale: ${locale}`);
        })
        .execute();
    }
  }

  getActiveLocale(): string {
    // NOTE: Done this way via the property on 'window' to break a circular dependency.
    return (window as any).NG_SHARED_USER_SERVICE.getLanguage();
  }

  getSystemDefaultCurrencyCode(): string {
    return (window as any).NG_SHARED_USER_SERVICE.getSystemDefaultCurrencyCode()
  }

  getLocaleCustomization(): LocaleCustomization {
    // NOTE: Done this way via the property on 'window' to break a circular dependency.
    const locale = this.getActiveLocale();
    const custom = (window as any).NG_SHARED_USER_SERVICE.getLocaleCustomizations().find(x => x.Locale === locale);
    return {
      Locale: locale,
      ShortDateFormat: custom?.ShortDateFormat ?? DateTimeFormatSkeleton.SHORT_DATE,
      LongDateFormat: custom?.LongDateFormat ?? DateTimeFormatSkeleton.LONG_DATE,
      ShortTimeFormat: custom?.ShortTimeFormat ?? DateTimeFormatSkeleton.SHORT_TIME,
      LongTimeFormat: custom?.LongTimeFormat ?? DateTimeFormatSkeleton.LONG_TIME,
      ShortDateTimeFormat: custom?.ShortDateTimeFormat ?? DateTimeFormatSkeleton.SHORT_DATE_TIME ,
      LongDateTimeFormat: custom?.LongDateTimeFormat ?? DateTimeFormatSkeleton.LONG_DATE_TIME ,

      ShortDateFormatIsSkeleton: custom?.ShortDateFormatIsSkeleton ?? true,
      LongDateFormatIsSkeleton: custom?.LongDateFormatIsSkeleton ?? true,
      ShortTimeFormatIsSkeleton: custom?.ShortTimeFormatIsSkeleton ?? true,
      LongTimeFormatIsSkeleton: custom?.LongTimeFormatIsSkeleton ?? true,
      ShortDateTimeFormatIsSkeleton: custom?.ShortDateTimeFormatIsSkeleton ?? true,
      LongDateTimeFormatIsSkeleton: custom?.LongDateTimeFormatIsSkeleton.value ?? true,
    };
  }

  async getLocales(): Promise<LocaleKeyValue[]> {

    const locale = this.getActiveLocale();
    // NOTE: Done this way via the property on 'window' to break a circular dependency.
    const requestService = (window as any).NG_SHARED_REQUEST_SERVICE;

    const response: any = await requestService.createRequest()
      .controller("servicely.core.Application")
      .method("getLocales")
      .data({locale})
      .execute();

    return response.data as LocaleKeyValue[];
  }

  // Culture

  getCulture(): string {
    // @ts-ignore
    return this.i18n.getCulture();
  }

  getBaseLanguageCode(): string {
    return this.baseLanguageCode;
  }

  getLanguageCode(locale: LocaleString): string {
      return this.underscoreLocale(locale).split("_")[0];
  }

  // Support for legacy date pickers
  getMonths(locale: LocaleString): string[] {
    const underscoreLocale = this.underscoreLocale(locale);
    const languageCode = this.getLanguageCode(locale);
    const months = this.SUPPORTED_LOCALES[underscoreLocale].calendar.main[languageCode].dates.calendars.gregorian.months['stand-alone'].wide;
    return [
      months[1],
      months[2],
      months[3],
      months[4],
      months[5],
      months[6],
      months[7],
      months[8],
      months[9],
      months[10],
      months[11],
      months[12],
    ];
  }

  getDaysOfWeek(locale: LocaleString): string[] {
    const underscoreLocale = this.underscoreLocale(locale);
    const languageCode = this.getLanguageCode(locale);
    const days = this.SUPPORTED_LOCALES[underscoreLocale].calendar.main[languageCode].dates.calendars.gregorian.days['stand-alone'].abbreviated;

    return [
      days['sun'],
      days['mon'],
      days['tue'],
      days['wed'],
      days['thu'],
      days['fri'],
      days['sat'],
    ];
  }

  // Messages

  getMessageWithFallback(keysArray: any[]): string {
    return (window as any).KO_INTERNATIONALIZATION_BRIDGE.getMessageWithFallback(keysArray);
  };

  getMessageWithDefault(key: string, defaultKey?: string, data?: object): string {
    return (window as any).KO_INTERNATIONALIZATION_BRIDGE.getMessageWithDefault(key, defaultKey, data);
  };

  getMessage(key: string, data?: object): string {
    return (window as any).KO_INTERNATIONALIZATION_BRIDGE.getMessage(key, data);
  };

  hasMessage(key: string): boolean {
    return (window as any).KO_INTERNATIONALIZATION_BRIDGE.hasMessage(key);
  }

  cacheMessage(key: string, message: string): string {
    return (window as any).KO_INTERNATIONALIZATION_BRIDGE.compileI18nMessage(key, message);
  }

  getI18nFieldValue(i18nKey: string, locale: string, message: string): InternationalizationFieldValue {
    return (window as any).KO_INTERNATIONALIZATION_BRIDGE.getI18nFieldValue(i18nKey, locale, message);
  }

  generateDefaultKey(tableName: string, fieldName: string): string {
    return (window as any).KO_INTERNATIONALIZATION_BRIDGE.generateDefaultKey(tableName, fieldName);
  }

  // Numbers

  getNumberFormat(options?: NumberFormatOptions): Function {
    return this.i18n.getNumberFormat(options);
  }

  getNumberParser(options?: NumberFormatOptions): Function {
    return this.i18n.getNumberParser(options);
  }

  parseNumber(value: string, option?: NumberFormatOptions): number {
    return this.i18n.parseNumber(value, option);
  }

  getNumberPattern(option: NumberFormatOptions, isExcel?: boolean): string {
    return this.i18n.getNumberPattern(option, isExcel);
  }

  formatNumber(value: number, option?: NumberFormatOptions): string {
    return this.i18n.formatNumber(value, option);
  }

  numberBuilder(): InternationalizationNumberFormatOptions {
    return new InternationalizationNumberFormatOptions();
  }

  // Dates

  getDateFormat(options?: DateFormatOptions): Function {
    return this.i18n.getDateFormat(options);
  }

  getDateParser(options?: DateFormatOptions): Function {
    return this.i18n.getDateParser(options);
  }

  parseDate(value: string, option?: DateFormatOptions): Date {
    return this.i18n.parseDate(value, option);
  }

  getDatePattern(option: DateFormatOptions, isExcelFormat?: boolean): string {
    return this.patchDatePattern(this.i18n.getDatePattern(option, isExcelFormat));
  }

  formatDate(value: Date, option?: DateFormatOptions): string {
    return this.i18n.formatDate(value, option);
  }

  dateBuilder(): InternationalizationDateFormatOptions {
    return new InternationalizationDateFormatOptions();
  }

  getFirstDayOfWeek(): number {
    // There is a bug in Syncfusion where it treats the language code as a country code for this query.
    // Therefore, for af_ZA it gives the first day of the week in Afghanistan (Sat)
    return (this.getActiveLocale() == "af_ZA") ? 0 : this.i18n.getFirstDayOfWeek();
  }

  isAMPMTime(): boolean {
    // There is no other obvious way to determine if a locale favours 12 hour over 24 hour
    return this.patchDatePattern(this.i18n.getDatePattern({type: 'time', skeleton: 'short'})).includes('a');
  }

  formatShortDate(value?: Date): string {
    return this.formatDate(value, this.getShortDateOptions());
  }

  formatLongDate(value?: Date): string {
    return this.formatDate(value, this.getLongDateOptions());
  }

  formatShortTime(value?: Date): string {
    return this.formatDate(value, this.getShortTimeOptions());
  }

  formatLongTime(value?: Date): string {
    return this.formatDate(value, this.getLongTimeOptions());
  }

  formatShortDateTime(value?: Date): string {
    return this.formatDate(value, this.getShortDateOptions())
    + " "
    + this.formatDate(value, this.getShortTimeOptions());
  }

  formatLongDateTime(value?: Date): string {
    return this.formatDate(value, this.getLongDateOptions())
      + " "
      + this.formatDate(value, this.getLongTimeOptions());
  }

  toLocaleDateOfTimezone(date: Date, timezone: string): Date {
    const luxonDateTime = DateTime.fromJSDate(date).setZone(timezone);

    // Will produce a new date representing a different epoch instant than the 'date' argument
    // in order to represent 'date' as the local time of 'timezone'
    // ie date: Fri Jul 21 2023 11:00:00 GMT+1000 (Australian Eastern Standard Time)
    // timezone: Pacific/Fiji, browser timezone: Australia/Sydney -> Fri Jul 21 2023 13:00:00 GMT+1000
    return luxonDateTime.setZone("local", { keepLocalTime: true }).toJSDate()
  }

  /**
   * Parses a string value into a DateTime object, considering the time zone provided.
   *
   * @todo Move the Luxon DateTime to a consistent API to DateTime on the Server
   * @todo Possibly move out of this file and into the date one above
   *
   * @param {string} value - The string value representing a date and time in ISO format, optionally including time zone information at the end (e.g., "+03:00", "-05:30", or "Z" for UTC).
   * @returns {DateTime} A Luxon DateTime object parsed from the input value.
   */
  parseIsoDateTimeWithZone(value: string): DateTime {
    const regex = /(Z|[\+\-]\d{2}:\d{2})$/;
    const match = value.match(regex);

    if (match) {
      let zonePart = match[1]; // If there's no zone part, it's 'Z' (UTC)
      const datePart = value.substring(0, value.length - zonePart.length);

      if (zonePart == "Z") {
        zonePart = "UTC"
      }

      return DateTime.fromISO(datePart, { zone: zonePart });
    } else {
      return DateTime.fromISO(value);
    }
  }

  /**
   * Formats the given DateTime object in long date format with time zone information.
   *
   * @param {DateTime} value - The DateTime object to be formatted.
   * @return {string} The formatted string including long date, time, and time zone.
   */
  formatLongDateTimeZone(value?: DateTime, options ?: any): string {

    options = options || {};

    let timeOnly = options.timeOnly === true;
    let excludeZone = options.excludeZone === true;

    // Remove all timezone related information and just get the Java DateTime
    let local = value.setZone("local", { keepLocalTime: true }).toJSDate();

    let result = "";

    if (!timeOnly) {
      result += this.formatDate(local, this.getLongDateOptions()) + " ";
    }

    result += this.formatDate(local, this.getLongTimeOptions());

    if (!excludeZone) {
      result += ` ${value.offsetNameShort}`;
    }

    return result;
  }

  /**
   * Checks if the given string datetime is in the current timezone.
   * @param {string} value1 - The string representing a datetime value.
   * @return {boolean} Returns true if the provided datetime is in the current timezone, false otherwise.
   */
  isStringDateTimeInCurrentTimezone(value1: string): boolean {
    let value1Date = this.parseIsoDateTimeWithZone(value1);
    let value2Date = DateTime.now();

    return value1Date.offset == value2Date.offset;
  }

  lookupLocale(locale?: LocaleString): LocalePair {
    const underscoreLocale = this.underscoreLocale(locale);
    const language = underscoreLocale && this.SUPPORTED_LOCALES[underscoreLocale] ? underscoreLocale : this.DEFAULT_LOCALE;
    const culture = this.SUPPORTED_LOCALES[language].culture;
    return {language, culture};
  }

  private getShortDateOptions(): DateFormatOptions {
    const custom = this.getLocaleCustomization();
    return custom.ShortDateFormatIsSkeleton ?
     {type: 'date', skeleton: custom.ShortDateFormat} :
      {type: 'date', format: custom.ShortDateFormat};
  }

  private getLongDateOptions(): DateFormatOptions {
    const custom = this.getLocaleCustomization();
    return custom.LongDateFormatIsSkeleton ?
      {type: 'date', skeleton: custom.LongDateFormat} :
      {type: 'date', format: custom.LongDateFormat};
  }

  private getShortTimeOptions(): DateFormatOptions {
    const custom = this.getLocaleCustomization();
    return custom.ShortTimeFormatIsSkeleton ?
      {type: 'time', skeleton: custom.ShortTimeFormat} :
      {type: 'time', format: custom.ShortTimeFormat};
  }

  private getLongTimeOptions(): DateFormatOptions {
    const custom = this.getLocaleCustomization();
    return custom.LongTimeFormatIsSkeleton ?
      {type: 'time', skeleton: custom.LongTimeFormat} :
      {type: 'time', format: custom.LongTimeFormat};
  }

  private getShortDateTimeOptions(): DateFormatOptions {
    const custom = this.getLocaleCustomization();
    return custom.ShortDateTimeFormatIsSkeleton ?
      {type: 'dateTime', skeleton: custom.ShortDateTimeFormat} :
      {type: 'dateTime', format: custom.ShortDateTimeFormat};
  }

  private getLongDateTimeOptions(): DateFormatOptions {
    const custom = this.getLocaleCustomization();
    return custom.LongDateTimeFormatIsSkeleton ?
      {type: 'dateTime', skeleton: custom.LongDateTimeFormat} :
      {type: 'dateTime', format: custom.LongDateTimeFormat};
  }

  // Temp methods until all uses of deprecated methods on the KO side is gone

  /**
   * @deprecated remove this when no longer used anywhere
   */
  getShortDateFormatCLDR(): string {
    return this.patchDatePattern(this.i18n.getDatePattern(this.getShortDateOptions()));
  }

  /**
   * @deprecated remove this when no longer used anywhere
   */
  getLongDateFormatCLDR(): string {
    return this.patchDatePattern(this.i18n.getDatePattern(this.getLongDateOptions()));
  }

  /**
   * @deprecated remove this when no longer used anywhere
   */
  getShortTimeFormatCLDR(): string {
    return this.patchDatePattern(this.i18n.getDatePattern(this.getShortTimeOptions()));
  }

  /**
   * @deprecated remove this when no longer used anywhere
   */
  getLongTimeFormatCLDR(): string {
    return this.patchDatePattern(this.i18n.getDatePattern(this.getLongTimeOptions()));
  }

  /**
   * @deprecated remove this when no longer used anywhere
   */
  getShortDateTimeFormatCLDR(): string {
    return this.patchDatePattern(this.i18n.getDatePattern(this.getShortDateOptions()))
      + " "
      + this.patchDatePattern(this.i18n.getDatePattern(this.getShortTimeOptions()));
  }

  /**
   * @deprecated remove this when no longer used anywhere
   */
  getLongDateTimeFormatCLDR(): string {
    return this.patchDatePattern(this.i18n.getDatePattern(this.getLongDateOptions()))
      + " "
      + this.patchDatePattern(this.i18n.getDatePattern(this.getLongTimeOptions()));
  }

  /**
   * @deprecated remove this when no longer used anywhere
   */
  getDigitsAfterDecimal(): number {
    return 2;
  }

  private underscoreLocale(locale: LocaleString): string {
    return locale ? locale.replace("-", "_") : locale;
  }

  private dashLocale(locale: LocaleString): string {
    return locale ? locale.replace("_", "-") : locale;
  }

  private patchDatePattern(datePattern: string): string {
    return datePattern?.replace(/ /g, ' ').replace(/ /g, ' ');
  }
}
